import XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { ModelFieldType, RuntimeModel, RuntimeModelField } from '@kunlun/dependencies';
import { removeDecimalZero } from './number';
import { isArray, isNil, omit } from 'lodash-es';

type CustomFieldsParser = Record<
  string,
  (val: any, dataSourceIndex: number, field: RuntimeModelField, row: any) => any
>;
interface ExportExcelFileOptions {
  filename: string;
  bookType?: XLSX.BookType;
  SheetNames?: string[];
}

interface ExportModelExcelFileOptions extends ExportExcelFileOptions {
  customFieldsParser?: CustomFieldsParser;
  addLineNumber?: boolean;
}

/**
 * 导出excel文件
 * 示例数据(key为表头，value为数据):
 * [{
 *  ERP商品编码: '123',
 *  商品名称: '商品1',
 *  物料组: '物料组1',
 *  结算扣率: '0.1',
 *  结算单价: '10',
 * }]
 *
 * @param {any[]} excelData        // 导出数据
 * @param {*} { filename, bookType = 'xlsx' }
 */
export const exportExcelFile = (excelData: any[], options: ExportExcelFileOptions) => {
  const { filename, bookType = 'xlsx' as XLSX.BookType, SheetNames = ['sheet1'] } = options;

  const sheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = {
    SheetNames,
    Sheets: {
      sheet1: sheet
    }
  };
  const wbout = XLSX.write(workbook, {
    bookType,
    bookSST: true,
    type: 'array'
  });
  FileSaver.saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename);
};

/**
 * 导出模型excel文件
 * 示例数据(key为表头，value为数据):
 * [{
 *  ERP商品编码: '123',
 *  商品名称: '商品1',
 *  物料组: '物料组1',
 *  结算扣率: '0.1',
 *  结算单价: '10',
 * }]
 *
 * @param {any[]} dataSource        // 导出数据
 * @param {RuntimeModel} model      // 表格的模型字段 - modelFields
 * @param {*} { customFieldsParser, filename, bookType = 'xlsx' }
 */
export const exportModelExcelFile = (dataSource: any[], model: RuntimeModel, options: ExportModelExcelFileOptions) => {
  const {
    customFieldsParser = {} as CustomFieldsParser,
    filename,
    bookType = 'xlsx' as XLSX.BookType,
    SheetNames = ['sheet1'],
    addLineNumber = false
  } = options;

  const exportFields = model.modelFields.filter((i) => !model.pks?.includes(i.data) && !i.invisible);

  // 没数据时支持导出表头
  let exportData = dataSource;
  if (!exportData.length) {
    exportData = [{}];
  }

  const excelData = exportData.map((i, dataSourceIndex) => {
    const obj: Record<string, any> = {};
    if(addLineNumber){
      obj['行号'] = dataSourceIndex + 1;
    }
    exportFields.forEach((field) => {
      let value = i[field.data];

      // 自定义处理字段逻辑
      if (typeof customFieldsParser[field.data] === 'function') {
        const parsedValue = customFieldsParser[field.data](value, dataSourceIndex, field, i);
        const label = parsedValue['label'] || field.label;
        obj[label] = parsedValue['value'] || parsedValue;
        return;
      }

      if (isNil(value)) {
        obj[field.label!] = value;
        return;
      }
      // 通用处理字段逻辑

      // 带option处理，比如 ENUM 和 BOOLEAN 类型
      // @ts-ignore
      if (field.options?.length) {
        // @ts-ignore
        const optionsArr = field.options.filter((i) => !i.invisible) ?? []
        if (isArray(value) && value.length > 1) {
          //处理多个标签
          value = optionsArr.filter(item => value.includes(item.name)).map(it => it.displayName).join('、')
        } else {
          //处理单个标签
          value = optionsArr.find((opt) => opt.name === value + '')?.displayName ?? '';
        }

      }
      // 其他类型处理
      else {
        switch (field.ttype) {
          case ModelFieldType.Float:
            value = removeDecimalZero(value);
            break;
          // more case
        }
      }

      obj[field.label!] = value;
    });
    return obj;
  });

  exportExcelFile(excelData, omit(options, 'customFieldsParser'));
};

/**
 * 点击上传文件
 *
 */
export const clickUploadExcelFile = async <T = any>(params?: { accept: HTMLInputElement['accept'] }) => {
  const { accept = '.xlsx, .xls' } = params || {};

  return new Promise<any[]>((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement)?.files?.[0];
      if (!file) {
        reject(new Error('No file selected'));
        return;
      }
      try {
        const data = await new Promise<any>((resolveFile) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const workbook = XLSX.read(e.target!.result, { type: 'binary', cellDates: true });
            const sheetNames = workbook.SheetNames;
            const worksheet = workbook.Sheets[sheetNames[0]];
            const sheetList = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false }) as T[];
            resolveFile(sheetList);
          };
          reader.readAsBinaryString(file);
        });
        resolve(data);
      } catch (error) {
        reject(error);
      }
    };
    input.click();
    input.remove();
  });
};
